import { Fragment } from 'react'
import { Box, Typography } from '@mui/material'
import PlannedExpertiseWorkflowQuestion from './PlannedExpertiseWorkflowQuestion'
import TextFreeQuestion from '../questionTypes/TextFreeQuestion'
import NumberFreeQuestion from '../questionTypes/NumberFreeQuestion'
import TransmissionSlider from '../questionTypes/TransmissionSlider'
import TransmissionDatePicker from '../questionTypes/TransmissionDatePicker'
import TransmissionSketcher from '../questionTypes/TransmissionSketcher'
import TransmissionPJ from '../questionTypes/TransmissionPJ/TransmissionPJ'
import SingleChoiceQuestion from '../questionTypes/SingleChoiceQuestion'
import MultipleChoiceQuestion from '../questionTypes/MultipleChoiceQuestion'
import TransmissionsAutoCalculated from '../questionTypes/TransmissionsAutoCalculated'
import { PlannedExpertiseWorkflowContentProps } from './_props'

export default function PlannedExpertiseWorkflowContent({
    currentQuestion,
    progress,
    requestAnswers,
    localAnswersByQuestionId,
    setLocalAnswersByQuestionId,
    isTextFreeQuestion,
    isNumberFreeQuestion,
    isSliderQuestion,
    isDateQuestion,
    isSketchQuestion,
    isChoiceQuestion,
    isMultipleChoiceQuestion,
    isMessageQuestion,
    handleSelectChoice,
    currentPatientSex,
}: PlannedExpertiseWorkflowContentProps) {
    const questionId = currentQuestion?.id ?? ''
    const isPJQuestion = (currentQuestion as any)?.inputType === 'pj'

    // once the question has a local entry (answered, emptied or skipped), the saved request answer is no longer used
    const hasLocalEntry = localAnswersByQuestionId.has(questionId)

    const getAnswerFromRequest = () =>
        hasLocalEntry ? undefined : (requestAnswers?.find((answer) => answer.id === currentQuestion?.id)?.value as any)

    const getAnswerFromLocal = () => localAnswersByQuestionId.get(questionId)?.[0] as any

    const setLocalAnswer = (val: any) => {
        setLocalAnswersByQuestionId((prev) => {
            const next = new Map(prev)
            if (!questionId) return next
            next.set(questionId, val ? [val] : [])
            return next
        })
    }

    function renderQuestionInput() {
        if (currentQuestion?.inputType === 'calculated') {
            return (
                <TransmissionsAutoCalculated
                    value={getAnswerFromLocal() ?? getAnswerFromRequest()}
                    execName={(currentQuestion as any)?.execName ?? ''}
                />
            )
        }

        if (isTextFreeQuestion) {
            return (
                <TextFreeQuestion
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                    }}
                    initialValue={getAnswerFromLocal() ?? getAnswerFromRequest() ?? null}
                    onAnswer={setLocalAnswer}
                />
            )
        }

        if (isNumberFreeQuestion) {
            return (
                <NumberFreeQuestion
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                        numberMin: (currentQuestion as any)?.numberMin,
                        numberMax: (currentQuestion as any)?.numberMax,
                        numberExpectedFormat: (currentQuestion as any)?.numberExpectedFormat,
                    }}
                    initialValue={getAnswerFromLocal() ?? getAnswerFromRequest() ?? null}
                    onAnswer={(val) => {
                        setLocalAnswersByQuestionId((prev) => {
                            const next = new Map(prev)
                            if (!questionId) return next
                            next.set(questionId, val === '' || val == null ? [] : [val])
                            return next
                        })
                    }}
                />
            )
        }

        if (isSliderQuestion) {
            return (
                <TransmissionSlider
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                        minimumValue: (currentQuestion as any)?.sliderMin,
                        maximumValue: (currentQuestion as any)?.sliderMax,
                        step: (currentQuestion as any)?.sliderStep,
                    }}
                    initialValue={
                        getAnswerFromLocal() ?? getAnswerFromRequest() ?? (currentQuestion as any)?.sliderMin ?? 0
                    }
                    onAnswer={(val) => {
                        setLocalAnswersByQuestionId((prev) => {
                            const next = new Map(prev)
                            if (!questionId) return next
                            next.set(questionId, [val])
                            return next
                        })
                    }}
                />
            )
        }

        if (isDateQuestion) {
            return (
                <TransmissionDatePicker
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                        dateMin: (currentQuestion as any)?.dateMin,
                        dateMax: (currentQuestion as any)?.dateMax,
                    }}
                    initialValue={getAnswerFromLocal() ?? getAnswerFromRequest() ?? null}
                    onAnswer={setLocalAnswer}
                />
            )
        }

        if (isPJQuestion) {
            return (
                <TransmissionPJ
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                    }}
                    initialValue={
                        hasLocalEntry ? localAnswersByQuestionId.get(questionId) : getAnswerFromRequest() ?? null
                    }
                    onAnswer={(val) => {
                        setLocalAnswersByQuestionId((prev) => {
                            const next = new Map(prev)
                            if (!questionId) return next
                            next.set(questionId, Array.isArray(val) ? val : [])
                            return next
                        })
                    }}
                />
            )
        }

        if (isSketchQuestion) {
            return (
                <TransmissionSketcher
                    question={{
                        id: currentQuestion?.id,
                        sectionLabel: currentQuestion?.sectionLabel,
                        title: currentQuestion?.title,
                        hint: currentQuestion?.hint,
                        required: currentQuestion?.required,
                        optional: currentQuestion?.optional,
                    }}
                    initialValue={getAnswerFromLocal() ?? getAnswerFromRequest() ?? null}
                    onAnswer={setLocalAnswer}
                    patientSex={currentPatientSex ?? null}
                />
            )
        }

        if (isChoiceQuestion && isMultipleChoiceQuestion) {
            return (
                <MultipleChoiceQuestion
                    question={{
                        choices: currentQuestion.choices,
                        required: !!currentQuestion.required,
                    }}
                    initialValue={
                        localAnswersByQuestionId.get(questionId) ??
                        (() => {
                            const rawAnswer = requestAnswers?.find((answer) => answer.id === currentQuestion.id)?.value
                            if (Array.isArray(rawAnswer)) return rawAnswer.map(String)
                            if (typeof rawAnswer === 'string') {
                                try {
                                    const parsed = JSON.parse(rawAnswer)
                                    if (Array.isArray(parsed)) return parsed.map(String)
                                } catch {}
                            }
                            return []
                        })()
                    }
                    onAnswer={(selected) => {
                        setLocalAnswersByQuestionId((prev) => {
                            const next = new Map(prev)
                            next.set(questionId, selected)
                            return next
                        })
                    }}
                />
            )
        }

        if (isChoiceQuestion) {
            return (
                <SingleChoiceQuestion
                    question={{
                        id: currentQuestion.id,
                        choices: currentQuestion.choices,
                        required: !!currentQuestion.required,
                        text: currentQuestion.title,
                    }}
                    initialValue={getAnswerFromLocal() ?? null}
                    onAnswer={(choice) => {
                        setLocalAnswersByQuestionId((prev) => {
                            const next = new Map(prev)
                            if (choice == null) next.set(questionId, [])
                            else next.set(questionId, [choice.id ?? choice])
                            return next
                        })
                        if (choice !== null) handleSelectChoice(choice.id ?? (choice as any))
                    }}
                />
            )
        }
        return null
    }

    if (!currentQuestion || progress.totalSteps === 0) {
        return (
            <Typography>
                {progress.isComplete ? 'Toutes les questions ont été répondues !' : 'Aucune question disponible'}
            </Typography>
        )
    }

    const questionInput = renderQuestionInput()

    return (
        <Fragment>
            <PlannedExpertiseWorkflowQuestion
                question={currentQuestion}
                currentStep={progress.currentStep}
                totalSteps={progress.totalSteps}
            />
            <Fragment key={questionId}>{questionInput}</Fragment>
        </Fragment>
    )
}
